import React, { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2 } from 'lucide-react';

// 18 preguntas estructuradas del perfil estratégico (PROMPT MAESTRO)
const QUESTIONS = [
  { id: 'industry', label: '1. Giro principal', type: 'text', placeholder: 'ej. Perfumería nicho, Cafetería de especialidad, Clínica dental' },
  { id: 'brand_positioning', label: '2. Posicionamiento de marca', type: 'select', options: ['premium', 'medio-alto', 'medio', 'accesible', 'popular'] },
  { id: 'ticket_promedio', label: '3. Ticket promedio (MXN)', type: 'number', placeholder: '350' },
  { id: 'sq_meters', label: '4. Superficie del local (m²)', type: 'number', placeholder: '80' },
  { id: 'staff_count', label: '5. Número de personal', type: 'number', placeholder: '4' },
  { id: 'hours_weekday', label: '6. Horario entre semana', type: 'text', placeholder: '10:00 - 21:00' },
  { id: 'hours_weekend', label: '7. Horario fin de semana', type: 'text', placeholder: '11:00 - 22:00' },
  { id: 'target_customer', label: '8. Cliente objetivo', type: 'textarea', placeholder: 'Mujer 25-45 años, ingreso medio-alto, interesada en productos exclusivos', rows: 2 },
  { id: 'purchase_frequency', label: '9. Frecuencia de compra esperada', type: 'select', options: ['diaria', 'semanal', 'quincenal', 'mensual', 'trimestral', 'esporádica'] },
  { id: 'seasonality', label: '10. Estacionalidad', type: 'select', options: ['sin estacionalidad', 'temporadas cortas (fiestas)', 'temporada alta invierno', 'temporada alta verano', 'fuerte estacionalidad'] },
  { id: 'competition_tolerance', label: '11. Tolerancia a competencia cercana', type: 'select', options: ['prefiere zona con competencia (clustering)', 'indistinto', 'prefiere zona sin competencia'] },
  { id: 'foot_traffic_dependency', label: '12. Dependencia de tráfico peatonal', type: 'select', options: ['alta - venta por impulso', 'media', 'baja - venta por cita/plan'] },
  { id: 'parking_criticality', label: '13. Estacionamiento crítico para ventas', type: 'select', options: ['sí', 'deseable', 'no'] },
  { id: 'depends_tourism', label: '14. ¿Depende de turismo?', type: 'select', options: ['sí', 'parcialmente', 'no'] },
  { id: 'target_income', label: '15. Nivel socioeconómico objetivo', type: 'select', options: ['A/B', 'C+', 'C', 'C-', 'D+', 'D/E'] },
  { id: 'monthly_fixed_costs', label: '16. Costos fijos mensuales estimados (MXN)', type: 'number', placeholder: '85000' },
  { id: 'investment_budget', label: '17. Presupuesto de inversión (MXN)', type: 'number', placeholder: '800000' },
  { id: 'business_model', label: '18. Modelo de negocio', type: 'select', options: ['sucursal propia', 'franquicia', 'joint venture', 'consignación', 'otro'] },
];

export default function ProfileForm({ analysisId, currentProfile, onSaved }) {
  const [form, setForm] = useState(() => {
    const init = {};
    QUESTIONS.forEach(q => { init[q.id] = currentProfile?.[q.id] ?? ''; });
    return init;
  });
  const [saving, setSaving] = useState(false);

  const set = (id, v) => setForm(prev => ({ ...prev, [id]: v }));

  const filled = QUESTIONS.filter(q => (form[q.id] ?? '').toString().trim().length > 0).length;
  const completeness = Math.round((filled / QUESTIONS.length) * 100);

  const save = async () => {
    if (filled < 3) return toast.error('Completa al menos 3 preguntas antes de guardar');
    setSaving(true);
    try {
      const profileId = currentProfile?.id || currentProfile?.business_profile_id;
      if (profileId) {
        await api.patch(`/business-profiles/${profileId}`, form);
      }
      // Also re-run industry score using the fresh profile
      await api.post(`/analyses/${analysisId}/industry-score`, {});
      toast.success(`Perfil guardado (${filled}/${QUESTIONS.length}) · recalculando score…`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="profile-form">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <div className="label-caps">Formulario estructurado · 18 preguntas</div>
          <div className="text-xs text-slate-500 mt-1">Cada respuesta refina el score específico para tu giro. Deja en blanco lo que no aplique.</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-display font-bold text-blue-900">{completeness}%</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{filled}/{QUESTIONS.length} completadas</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[55vh] overflow-y-auto pr-2">
        {QUESTIONS.map(q => (
          <div key={q.id} className="space-y-1">
            <Label htmlFor={`pf-${q.id}`} className="text-xs font-semibold">{q.label}</Label>
            {q.type === 'textarea' ? (
              <Textarea
                id={`pf-${q.id}`}
                data-testid={`pf-${q.id}`}
                value={form[q.id] || ''}
                onChange={(e) => set(q.id, e.target.value)}
                placeholder={q.placeholder}
                rows={q.rows || 2}
                className="text-sm"
              />
            ) : q.type === 'select' ? (
              <select
                id={`pf-${q.id}`}
                data-testid={`pf-${q.id}`}
                value={form[q.id] || ''}
                onChange={(e) => set(q.id, e.target.value)}
                className="w-full border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900"
              >
                <option value="">— Seleccionar —</option>
                {q.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <Input
                id={`pf-${q.id}`}
                data-testid={`pf-${q.id}`}
                type={q.type}
                value={form[q.id] || ''}
                onChange={(e) => set(q.id, e.target.value)}
                placeholder={q.placeholder}
                className="text-sm"
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
        <Button onClick={save} disabled={saving} data-testid="profile-form-save">
          {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />}
          Guardar y recalcular score
        </Button>
      </div>
    </div>
  );
}
